import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getTableLaterOrders, checkTableNumberExists, updateTableStatusById } from "apiHelpers/api/ApiHelpers";
import { useCustomHistory } from "hooks";
import {
  setIsSplitted,
  setExitingOrderDetails,
  setPreviousDiscountApplied,
  setTableInfo,
  clearSplit
} from "store/checkout/cart/cartSlice";
import {
  setSplitedItems,
  setOpenSplitBill,
  setOrdered
} from "store/checkout/order/orderSlice";
import { UTILS } from "utils";

function mergeItemsByOrderAndItemId(items) {
  const map = new Map();

  for (const item of items) {
    const key = `${item.orderId}-${item.itemId}`;
    if (map.has(key)) {
      const existing = map.get(key);
      existing.selectedQunatity += item.selectedQunatity;
    } else {
      map.set(key, { ...item });
    }
  }

  return Array.from(map.values());
}

const mergeItemsByItemId = (items) => {
  const map = new Map();
  for (const item of items) {
    const key = item.itemId;
    if (map.has(key)) {
      map.get(key).quantity += item.quantity || 0;
    } else {
      map.set(key, { ...item });
    }
  }
  return Array.from(map.values());
}

const usePayLaterOrders = (brandInfo) => {
  const { DomainType } = useSelector((state) => state.AppReducer);
  const dispatch = useDispatch();
  const location = useLocation();
  const router = useCustomHistory();
  const [error, setError] = useState(null);
  const brandId = brandInfo?.brand?._id;

  const queryParams = new URLSearchParams(location.search);
  const tableNumber = queryParams.get("table-number");
  const storeId = queryParams.get("store-id");

  const user= UTILS.getCurrentUser(brandInfo?.brand?._id);

  const PayLaterTab = () => {
    if (brandInfo?.setting?.temporaryDomainName) {
      router(
        `/dine-in/paylater`,
        `/${brandInfo.setting.temporaryDomainName}/dine-in/paylater`,
        DomainType,
        null,
        `?${queryParams}`,
      );
    }
  };

  const notFoundTable = () => {
    if (brandInfo?.setting?.temporaryDomainName) {
    router(
      `/dine-in/table-not-found`,
      `/${brandInfo.setting.temporaryDomainName}/dine-in/table-not-found`,
      DomainType,
      null,
      `?${queryParams}`,
    );
    }
  };

  const fetchPayLaterOrders = async () => {
    try {
      const response = getTableLaterOrders({ brandId, tableNumber, storeId });
      const result = await response;
      const fetchedOrders = result?.data?.fetchedOrder ?? [];
      const splitBills = result?.data?.splitBills ?? [];
      const splitPayed = parseFloat(
        splitBills
          .reduce((total, bill) => total + (bill.amount || 0), 0)
          .toFixed(2),
      );
      dispatch(
        setExitingOrderDetails({
          orderId: fetchedOrders?.[0]?._id,
          orderNumber: fetchedOrders?.[0]?.orderNumber,
          dineInTableNumber: tableNumber,
        }),
      );
      dispatch(
        setPreviousDiscountApplied({
          ...fetchedOrders?.[0]?.discountApplied,
        }),
      );
      if (splitBills?.length > 0) {
        let splitedItems = [];
        splitBills?.forEach((bill) => {
          splitedItems.push(...bill?.selectedItems);
        });
        splitedItems = mergeItemsByOrderAndItemId(splitedItems);
        dispatch(setSplitedItems(splitedItems));
      } else {
        dispatch(setSplitedItems([]));
        dispatch(setIsSplitted(false));
      }
      const ordersTotalPrice = fetchedOrders
        ? parseFloat(
            fetchedOrders
              .reduce(
                (total, order) => total + (order?.totalOrderPrice || 0),
                0,
              )
              .toFixed(2),
          )
        : 0;
      if (fetchedOrders) {
        dispatch(
          setOrdered({
            splitBills,
            splitPayed,
            orderesTotalItemsPrice: parseFloat(
              fetchedOrders
                .reduce(
                  (total, order) => total + (order?.itemsTotalPrice || 0),
                  0,
                )
                .toFixed(2),
            ),
            ordersTotalPrice,
            ordersTotalTax: parseFloat(
              fetchedOrders
                .reduce((total, order) => {
                  const itemsTotalPrice = order?.itemsTotalPrice || 0;
                  const taxRate = order?.taxRate || 0;
                  const taxAmount = (itemsTotalPrice * taxRate) / 100;
                  return total + (isNaN(taxAmount) ? 0 : taxAmount);
                }, 0)
                .toFixed(2),
            ),
            totalTipAmount: parseFloat(
              fetchedOrders
                .reduce((total, order) => total + order.tipAmount, 0)
                .toFixed(2),
            ),
            allOrders: fetchedOrders.map((fo) => ({
              selectedPaymentMethod: fo.selectedPaymentMethod,
              ...(fo.selectedTip && {
                selectedTip: fo.selectedTip,
              }),
              paymentType: fo.paymentType,
              discountApplied: fo.discountApplied,
              itemsTotalPrice: fo.itemsTotalPrice,
              orderCurrency: fo.orderCurrency,
              orderedMenu: mergeItemsByItemId(fo.orderedMenu),
              orderNumber: fo.orderNumber,
              orderPlacedTime: fo.orderPlacedTime,
              totalOrderPrice: fo.totalOrderPrice,
              taxRate: fo.taxRate,
              _id: fo._id,
            })),
          }),
        );
        if (fetchedOrders.length > 0) {
          dispatch(setIsSplitted(splitBills?.length > 0 || false));
          PayLaterTab();
        } else setIsSplitted(false);
      } else {
        dispatch(setOrdered({}));
        setIsSplitted(false);
        dispatch(setSplitedItems([]));
        dispatch(clearSplit());
      }
    } catch (err) {
      setError(err);
      setIsSplitted(false);
    }
  };

  const getPayLaterTable = async () => {
    try {
      const response = await checkTableNumberExists({
        brandId,
        tableNumber,
        storeId,
      });
      if (response?.success && response?.data?._id) {
        dispatch(setTableInfo(response?.data));
        if(response?.data?.tableStatus?.toLowerCase() === "available") {
          await updateTableStatusById({
            brandId,
            tableNumber,
            storeId,
            _id: response?.data?._id,
            userId: user?.userId,
            tableStatus: "OCCUPIED",
          });
        }
        await fetchPayLaterOrders();
      } else {
        notFoundTable();
      }
    } catch (err) {
      notFoundTable();
    }
  };

  useEffect(() => {
    dispatch(setOpenSplitBill(false));
    dispatch(setTableInfo(null));
    if(location.pathname.includes("/dine-in/table-not-found")) {
      window.location.replace(window.location.pathname.replace("/dine-in/table-not-found", "/dine-in")+`?${queryParams}`);
    }
    if (location.pathname.includes("/dine-in") && brandId && tableNumber) {
      getPayLaterTable();
    }
  }, [brandId, tableNumber, storeId]);

  return { error };
};

export default usePayLaterOrders;
